// Typed fetch client to Worker /api routes.
// Types here mirror worker/types.ts — keep them in sync.

export type ArticleStatus = 'draft' | 'in_review' | 'approved' | 'published' | 'archived'
export type ApprovalStatus = 'pending' | 'approved' | 'revision_requested' | 'rejected'
export type AgentStatus = 'idle' | 'working' | 'blocked'

export interface Article {
  id: string
  title: string
  slug: string
  status: ArticleStatus
  type: 'post' | 'guide' | 'newsletter' | 'social'
  writer_id: string | null
  agent_id: string | null
  word_count: number
  body_md: string
  excerpt: string | null
  scheduled_for: string | null
  published_at: string | null
  wp_post_id: number | null
  topic_cluster: string | null
  target_keywords: string[]
  created_at: string
  updated_at: string
}

export interface Approval {
  id: string
  article_id: string
  article_title: string
  writer_name: string | null
  agent_name: string | null
  status: ApprovalStatus
  word_count: number
  type: Article['type']
  created_at: string
}

export interface Writer {
  id: string
  name: string
  email: string
  avatar_initials: string
  rate_cents: number
  rate_unit: 'piece' | 'word'
  monthly_budget_cents: number
  specialty: string | null
  status: 'active' | 'paused' | 'archived'
  // aggregated
  spent_cents: number
  pieces_mtd: number
}

export interface Agent {
  id: string
  name: string
  role: string
  status: AgentStatus
  avatar_color: string
  topics: string[]
}

export interface AgentActivity {
  id: string
  agent_id: string
  agent_name: string
  kind: string
  summary: string
  related_article_id: string | null
  severity: 'info' | 'success' | 'warning' | 'error'
  created_at: string
}

export interface Kpis {
  pending_approvals: number
  overdue_approvals: number
  scheduled_this_week: number
  scheduled_posts: number
  scheduled_social: number
  spend_mtd_cents: number
  budget_mtd_cents: number
  organic_sessions_28d: number
  organic_delta_pct: number
}

export interface SeoPulse {
  impressions_28d: number
  impressions_delta_pct: number
  avg_position: number
  avg_position_delta: number
  top_movers: Array<{ page: string; delta_pct: number }>
}

export interface GaRow {
  dimensionValues: Array<{ value: string }>
  metricValues: Array<{ value: string }>
}

export interface GaOverview {
  dimensionHeaders: Array<{ name: string }>
  metricHeaders: Array<{ name: string; type: string }>
  rows: GaRow[]
  totals: { sessions: number; users: number; pageviews: number }
}

export interface TopPage {
  page: string
  sessions: number
  users: number
  pageviews: number
  avg_engagement_seconds: number
}

export interface GscRow {
  keys: [string, string]
  clicks: number
  impressions: number
  ctr: number
  position: number
}

export interface GscResponse {
  responseAggregationType: string
  rows: GscRow[]
}

export interface RssFeedRef {
  agent_id: string
  agent_name: string
  topics: string[]
  feed_url: string
}

export type SocialPlatform = 'x' | 'linkedin' | 'facebook' | 'instagram'
export type SocialStatus = 'draft' | 'in_review' | 'approved' | 'scheduled' | 'published'

export interface SocialPost {
  id: string
  article_id: string | null
  article_title: string | null
  platform: SocialPlatform
  body: string
  status: SocialStatus
  scheduled_for: string | null
  published_at: string | null
  external_id: string | null
  created_at: string
}

export interface Invoice {
  id: string
  writer_id: string
  writer_name: string
  avatar_initials: string
  period_start: string
  period_end: string
  amount_cents: number
  pieces_count: number
  status: 'open' | 'submitted' | 'paid'
  submitted_at: string | null
  paid_at: string | null
  created_at: string
}

export interface WebhookDelivery {
  id: string
  target: string
  event: string
  related_id: string
  url: string
  request_body: string
  response_status: number
  response_body: string
  succeeded_at: string | null
  created_at: string
}

export interface Brief {
  id: string
  kind: 'content' | 'strategy'
  title: string
  target_audience: string | null
  target_keywords: string[]
  assigned_writer_id: string | null
  assigned_agent_id: string | null
  status: 'draft' | 'ready' | 'sent' | 'completed'
  body_md: string
  due_at: string | null
  created_at: string
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (res.status === 401) {
    window.location.reload()
    throw new Error('Unauthorized')
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? `${res.status}`)
  }
  return res.json() as Promise<T>
}

export const api = {
  // Dashboard
  kpis: () => request<Kpis>('/api/dashboard/kpis'),
  seoPulse: () => request<SeoPulse>('/api/analytics/seo-pulse'),
  agentActivity: () => request<AgentActivity[]>('/api/agents/activity'),

  // Analytics
  analyticsOverview: () => request<GaOverview>('/api/analytics/overview'),
  topPages: () => request<TopPage[]>('/api/analytics/top-pages'),
  gsc: () => request<GscResponse>('/api/analytics/gsc'),

  // Articles
  articles: (params?: { status?: ArticleStatus; from?: string; to?: string }) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString()
    return request<Article[]>(`/api/articles${qs ? `?${qs}` : ''}`)
  },
  article: (id: string) => request<Article>(`/api/articles/${id}`),
  updateArticle: (id: string, patch: Partial<Article>) =>
    request<Article>(`/api/articles/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  // Approvals
  approvals: () => request<Approval[]>('/api/approvals'),
  approve: (id: string) => request<{ ok: boolean; delivery_id: string; status: number }>(`/api/approvals/${id}/approve`, { method: 'POST' }),
  requestRevision: (id: string, note: string) =>
    request<{ ok: boolean }>(`/api/approvals/${id}/revision`, { method: 'POST', body: JSON.stringify({ note }) }),
  deliveries: () => request<WebhookDelivery[]>('/api/wordpress/deliveries'),

  // Writers
  writers: () => request<Writer[]>('/api/writers'),
  invoices: () => request<Invoice[]>('/api/writers/invoices'),

  // Agents
  agents: () => request<Agent[]>('/api/agents'),
  agentBlockReason: (id: string) =>
    request<{
      summary: string
      created_at: string
      related_article_id: string | null
      article_title: string | null
    }>(`/api/agents/${id}/block-reason`),

  // RSS
  rssIndex: () => request<RssFeedRef[]>('/api/rss'),

  // Social
  socialPosts: () => request<SocialPost[]>('/api/social'),
  approveSocial: (id: string) =>
    request<{ ok: boolean }>(`/api/social/${id}/approve`, { method: 'POST' }),
  scheduleSocial: (id: string, scheduled_for: string) =>
    request<{ ok: boolean }>(`/api/social/${id}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduled_for }),
    }),

  // Briefs
  briefs: () => request<Brief[]>('/api/briefs'),
  generateBrief: (input: {
    kind: 'content' | 'strategy'
    title: string
    audience?: string
    keywords?: string[]
  }) => request<{ brief: Brief }>('/api/briefs/generate', { method: 'POST', body: JSON.stringify(input) }),
}
